import React, {
	forwardRef,
	ForwardRefRenderFunction,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import Quill from 'quill';

import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';

import * as S from './RichTextEditor.styles';
import { Button, Text, useFloatingUi } from '@nicheinc/campus-ds';

import {
	TagsInputTooltip,
	TagsInputUtils,
	TagsInputConstants,
	TagsInputOptionsType,
} from '@global/design-system/atoms';
import { FloatingPortal } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { Delta } from 'quill/core';

const {
	checkHasCurlyBracesMergeTags,
	checkHasSquareBracketMergeTags,
	checkMergeTagsHasSpacingWarning,
	cleanTextWithSpaceRemoval,
	getCharacterCountWithMergeTags,
} = TagsInputUtils;

const {
	GrammarWarningEnum,
	MERGE_TAG_CHARACTER_COUNT,
	MERGE_TAG_INVALID_FORMAT_WARNING,
} = TagsInputConstants;

type OptionsType = TagsInputOptionsType;

type PropsType = {
	id?: string;
	readOnly?: boolean;
	initialValue?: any;
	placeholder?: string;
	hasError?: boolean;
	tagOptions?: OptionsType[];
	tagMenuText?: string;
	message?: string;
	noMessages?: boolean;
	characterLimit?: number;
	onTextChange?: (
		value: {
			html: string;
			string: string;
			delta?: Delta;
			characterCount?: number;
		},
		id?: string,
		...QuillArgs: any[]
	) => void;
};

const RichTextEditor = (
	{
		readOnly,
		initialValue,
		onTextChange,
		placeholder,
		id,
		hasError,
		tagOptions = [],
		tagMenuText = '+',
		characterLimit,
		message,
		noMessages,
	}: PropsType,
	ref: { current: Quill | null } | null,
) => {
	const internalQuillRef = useRef<Quill | null>(null);
	const quillRef = ref || internalQuillRef;

	const containerRef = useRef(null);
	const initialValueRef = useRef(initialValue);
	const onTextChangeRef = useRef(onTextChange);

	const [grammarWarnings, setGrammarWarning] = useState({
		[GrammarWarningEnum.MERGE_TAG_SPACE_AROUND]: false,
		[GrammarWarningEnum.MERGE_TAG_SQUARE_BRACKET]: false,
		[GrammarWarningEnum.MERGE_TAG_CURLY_BRACES]: false,
	});

	const handleInputWarnings = (text: string) => {
		const hasSpacingWarning = checkMergeTagsHasSpacingWarning(text);
		const hasSquareBracketMergeTag = checkHasSquareBracketMergeTags(text);
		const hasCurlyBracesMergeTag = checkHasCurlyBracesMergeTags(text);

		const warnings = {
			...grammarWarnings,
			[GrammarWarningEnum.MERGE_TAG_SPACE_AROUND]: hasSpacingWarning,
			[GrammarWarningEnum.MERGE_TAG_SQUARE_BRACKET]: hasSquareBracketMergeTag,
			[GrammarWarningEnum.MERGE_TAG_CURLY_BRACES]: hasCurlyBracesMergeTag,
		};

		setGrammarWarning(warnings);
	};

	const grammarWarningsObj = useMemo(() => grammarWarnings, [grammarWarnings]);

	const hasWarning = useMemo(
		() =>
			grammarWarningsObj[GrammarWarningEnum.MERGE_TAG_SPACE_AROUND] ||
			grammarWarningsObj[GrammarWarningEnum.MERGE_TAG_SQUARE_BRACKET] ||
			grammarWarningsObj[GrammarWarningEnum.MERGE_TAG_CURLY_BRACES],
		[grammarWarningsObj],
	);

	const textsColor = hasError
		? 'error.text.default'
		: hasWarning
			? 'warning.text.default'
			: 'system.text.medium';

	// Insert text into the editor
	const insertTag = (tag: { label: string; value: string }) => {
		setIsTooltipOpened(false);
		const range = quillRef?.current?.selection?.savedRange;

		if (range && characterLimit) {
			const currentText = quillRef?.current?.getText() || '';
			const currentLength = getCharacterCountWithMergeTags(currentText);

			// Check if adding this merge tag would exceed the character limit
			if (currentLength + MERGE_TAG_CHARACTER_COUNT > characterLimit) {
				return; // Prevent insertion if it would exceed the limit
			}
		}

		if (range) {
			quillRef?.current?.insertText(range.index, ` {{{${tag?.value}}}} `); // Insert the text at the current cursor position
		}
	};

	// Merge tags opening using floating UI
	const {
		x,
		y,
		openingReference,
		floatingReference,
		strategy,
		internalRefs: _internalRefs,
		isOpen: isTooltipOpened,
		setIsOpen: setIsTooltipOpened,
		getFloatingProps,
		getReferenceProps,
	} = useFloatingUi({
		preferredPlacement: 'bottom-start',
	});

	useLayoutEffect(() => {
		onTextChangeRef.current = onTextChange;
	});

	useEffect(() => {
		if (quillRef && quillRef.current) {
			quillRef.current?.enable(!readOnly);
		}
	}, [quillRef, readOnly]);

	useEffect(() => {
		const container = containerRef.current as unknown as HTMLElement;
		const editorContainer = container?.appendChild(
			container.ownerDocument.createElement('div'),
		);

		const quill = new Quill(editorContainer, {
			theme: 'snow',
			placeholder: placeholder,
			modules: {
				toolbar: {
					container: [
						[{ header: [1, 2, 3, false] }],
						['bold', 'italic', 'underline', 'strike'],
						['link'],
						[{ list: 'ordered' }, { list: 'bullet' }],
					],
				},
				keyboard: {
					bindings: {
						...(characterLimit
							? {
									// Character limit enforcement for typing
									characterLimit: {
										key: '^.$', // Match any single character
										handler: function (_range: any, _context: any) {
											const text = quill.getText();
											const currentLength =
												getCharacterCountWithMergeTags(text);
											return currentLength < characterLimit;
										},
									},
								}
							: {}),
					},
				},
				clipboard: {
					matchers: [
						[
							Node.ELEMENT_NODE,
							function (
								node: any,
								delta: {
									compose: (arg0: Delta) => any;
									length: () => number | Record<string, unknown>;
								},
							) {
								return delta.compose(
									new Delta().retain(delta.length(), {
										color: false,
										background: false,
									}),
								);
							},
						],
						...(characterLimit
							? [
									[
										Node.TEXT_NODE,
										function (node: any, delta: Delta) {
											const currentText = quill.getText();
											const currentLength =
												getCharacterCountWithMergeTags(currentText);
											const remainingChars = characterLimit - currentLength;

											if (remainingChars <= 0) {
												return new Delta(); // Block paste if at limit
											}

											// For text nodes, the delta should contain the text to be inserted
											if (delta.ops && delta.ops.length > 0) {
												const firstOp = delta.ops[0];
												if (typeof firstOp.insert === 'string') {
													const pastedText = firstOp.insert;

													if (pastedText.length > remainingChars) {
														const truncatedText = pastedText.substring(
															0,
															remainingChars,
														);
														return new Delta().insert(
															truncatedText,
															firstOp.attributes,
														);
													}
												}
											}

											return delta;
										},
									],
								]
							: []),
					],
				},
			},
		});

		quillRef.current = quill;

		if (initialValueRef.current) {
			const deltaConverted = quill.clipboard.convert({
				html: initialValueRef.current,
			});
			quill.setContents(deltaConverted, 'silent');
		}

		// Helper function to add <br> inside empty paragraph tags
		const addBrToEmptyParagraphs = (html: string): string => {
			// Replace empty paragraph tags with <p><br></p>
			return html.replace(/<p[^>]*>\s*<\/p>/gi, '<p><br></p>');
		};

		// Handle text changes for character count updates and callbacks
		quill.on(
			Quill.events.TEXT_CHANGE,
			(delta, oldDelta, source, ...QuillArgs) => {
				const text = quillRef?.current?.getText() || '';
				const currentLength = getCharacterCountWithMergeTags(text);

				// Fallback - When we reached the limit, we revert to previous state
				if (
					characterLimit &&
					currentLength > characterLimit &&
					source === 'user'
				) {
					quillRef?.current?.setContents(oldDelta, 'silent'); // Revert to previous state

					const revertedText = quillRef?.current?.getText() || '';
					const revertedLength = getCharacterCountWithMergeTags(revertedText);

					const rawHtml = quillRef?.current?.getSemanticHTML() || '';
					const processedHtml = addBrToEmptyParagraphs(
						cleanTextWithSpaceRemoval(rawHtml),
					);

					onTextChangeRef.current?.(
						{
							html: processedHtml,
							string: revertedText,
							delta: quillRef?.current?.getContents(),
							characterCount: revertedLength,
						},
						id,
						source,
						...QuillArgs,
					);

					handleInputWarnings(revertedText);
					return;
				}

				const rawHtml = quillRef?.current?.getSemanticHTML() || '';
				const processedHtml = addBrToEmptyParagraphs(
					cleanTextWithSpaceRemoval(rawHtml),
				);

				onTextChangeRef.current?.(
					{
						html: processedHtml,
						string: text,
						delta: quillRef?.current?.getContents(),
						characterCount: currentLength,
					},
					id,
					delta,
					source,
					...QuillArgs,
				);

				handleInputWarnings(text);
			},
		);

		return () => {
			quillRef.current = null;
			container.innerHTML = '';
		};
	}, []);

	const toolBarModule = quillRef?.current?.getModule('toolbar') as any;
	const toolbarContainer = toolBarModule?.container || document.body;

	return (
		<>
			<S.REWrapper ref={containerRef} id={id} hasError={hasError}>
				{!noMessages && (message || hasWarning) && (
					<Text
						size="small"
						lineHeight="large"
						color={textsColor}
						className="mt-2"
					>
						{message ||
							(grammarWarningsObj[GrammarWarningEnum.MERGE_TAG_SPACE_AROUND] &&
								'Please review spacing around merge tag(s)') ||
							((grammarWarningsObj[
								GrammarWarningEnum.MERGE_TAG_SQUARE_BRACKET
							] ||
								grammarWarningsObj[
									GrammarWarningEnum.MERGE_TAG_CURLY_BRACES
								]) &&
								MERGE_TAG_INVALID_FORMAT_WARNING)}
					</Text>
				)}
			</S.REWrapper>

			{/* Insert merge tags button in toolbar through portal */}
			{createPortal(
				<>
					{tagOptions && tagOptions.length ? (
						<span
							className="ql-formats insert-merge-tags"
							ref={openingReference}
							{...getReferenceProps()}
						>
							<Button
								size="large"
								variant="ghost"
								className="w-auto"
								onClick={() => setIsTooltipOpened(true)}
							>
								{`${tagMenuText}`}
							</Button>
						</span>
					) : null}
				</>,
				toolbarContainer,
			)}

			<FloatingPortal>
				{isTooltipOpened && (
					<TagsInputTooltip
						hideTabs={true}
						tooltipRef={floatingReference}
						style={{
							position: strategy,
							top: y ?? 0,
							left: x ?? 0,
						}}
						tagOptions={tagOptions as OptionsType[]}
						onSelect={(tag) => insertTag(tag as OptionsType)}
						{...getFloatingProps()}
					/>
				)}
			</FloatingPortal>
		</>
	);
};

export default forwardRef(
	RichTextEditor as ForwardRefRenderFunction<Quill, PropsType>,
);
export type { PropsType as RichTextEditorProps };
